/* eslint-disable @typescript-eslint/no-unused-vars, no-unneeded-ternary, max-len */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useRef, useEffect } from 'react';
import {
  useForm,
  FormProvider,
  Controller,
} from 'react-hook-form';
import { Signature, SignatureType, SignatureValue } from '@panna/sign';
import * as yup from 'yup';
import ElementUtils from 'src/components/elements/utils';
import { ElementError } from 'src/components/elements/types';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import { AuthorizationFormViewRendererProps, DynamicValues } from '../../types';
import logo from '../../../../images/dark.png';
import Footer from '../../../../components/PublicPageFooter';
import { Authform } from './types';

const schema = yup.object().shape({
  signature: yup.mixed<any>(),
});

function SignatureButton(props: { disabled: boolean }) {
  const { disabled } = props;
  return disabled ? (
    <div className="underline text-[#C0C0C0] font-semibold cursor-pointer">Click Here To Sign</div>
  ) : (
    <div className="underline text-primary font-semibold cursor-pointer">Click Here To Sign</div>
  );
}

function AuthForm(props: AuthorizationFormViewRendererProps) {
  const {
    details,
    onOpenTermsAndConditionsPopup,
    onOpenFeedbackPopup,
    submitSignature,
    termsAgreed,
  } = props;
  const authorizationRef = useRef<HTMLDivElement>(null);

  const methods = useForm<Authform>({
    defaultValues: { signature: undefined },
    resolver: useYupValidationResolver(schema),
  });

  const {
    control,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = methods;

  const replaceDynamicKeysWithDynamicValues = (
    defaultTemplate: string,
    dynamicValues: DynamicValues,
  ): string => {
    let template = defaultTemplate;
    Object.entries(dynamicValues).forEach(([key, value]) => {
      template = template.replaceAll(key, value);
    });
    return template;
  };

  useEffect(() => {
    if (authorizationRef.current) {
      const { mailTemPlate } = details || {};
      const updatedDynamicValues: DynamicValues = {
        '[$JOBLOCATION$]': details?.['[$JOBLOCATION$]'] || '',
        '[$JOBTITLE$]': details?.['[$JOBTITLE$]'] || '',
        '[$CLIENT_NAME$]': details?.['[$CLIENT_NAME$]'] || '',
        '[$JOBDESCRIPTION$]': details?.['[$JOBDESCRIPTION$]'] || '',
        '[$CANDIDATE_NAME$]': details?.['[$CANDIDATE_NAME$]'] || '',
        '[$CLIENT_LOCATION$]': details?.['[$CLIENT_LOCATION$]'] || '',
        '[$PAY_RATE$]': details?.['[$PAY_RATE$]'] || '',
        '[$PAY_TYPE$]': details?.['[$PAY_TYPE$]'] || '',
        '[$CANDIDATE_PHONE$]': details?.['[$CANDIDATE_PHONE$]'] || '',
        '[$CANDIDATE_EMAIL$]': details?.['[$CANDIDATE_EMAIL$]'] || '',
        '[$JOBTYPE$]': details?.['[$JOBTYPE$]'] || '',
        '[$JOBDURATION$]': details?.['[$JOBDURATION$]'] || '',
        '[$JOBID$]': details?.['[$JOBID$]'] || '',
      };
      authorizationRef.current.innerHTML = replaceDynamicKeysWithDynamicValues(mailTemPlate || '', updatedDynamicValues);
    }
    try {
      if (details && typeof details?.digitalSignature === 'string') {
        const signatureData: SignatureValue = JSON.parse(details?.digitalSignature);
        reset({
          signature: signatureData?.type ? signatureData : undefined,
        });
      }
    } catch (e) {
      console.log(e);
    }
  }, [authorizationRef.current, details]);

  const submitHandler = (signature: SignatureValue): void => {
    // Check for HR signature - whether available or not
    if (!signature?.type) {
      setError('signature', { type: 'required', message: 'Signature is required' });
      return;
    }
    const successCallback = (): void => {
      console.log('success');
    };
    const errorCallback = (): void => {
      reset({
        signature: undefined,
      });
    };
    submitSignature(signature, successCallback, errorCallback);
  };

  return (
    <form className="max-w-5xl mx-auto py-4">
      <FormProvider {...methods}>
        <div className="flex py-4">
          {details && <img src={details?.clientLogo || logo} width="180px" alt="logo" draggable="false" />}
        </div>
        <div className="text-left font-medium">
          <div className="text-base child:!font-[serif]" ref={authorizationRef} />
          <div className="h-[45px] mt-6">
            <Controller
              control={control}
              name="signature"
              render={({ field: { onChange, value } }) => (
                <Signature
                  user={{ name: details?.['[$CANDIDATE_NAME$]'] || '' }}
                  initType={SignatureType.digital}
                  value={value}
                  disableUploadSignature
                  onChange={(event) => {
                    onChange(event);
                    if (event) {
                      clearErrors('signature');
                      if (event.type) {
                        submitHandler(event);
                      }
                    }
                  }}
                  readonly={value?.type || !termsAgreed ? true : false}
                  styles={{
                    root: { textAlign: 'left', display: 'flex', alignItems: 'flex-end' },
                    digitalSignature: { fontSize: '1.2rem' },
                  }}
                  renderButton={() => <SignatureButton disabled={!termsAgreed} />}
                />
              )}
            />
            <span className="text-xs text-secondary mt-4">{details?.['[$CANDIDATE_NAME$]'] || ''}</span>
          </div>
          {errors?.signature && (
            <div className="text-error text-xs">
              {ElementUtils.renderError(errors.signature as ElementError)}
            </div>
          )}
          {
            !methods.getValues('signature')?.type && (
              <>
                {/* <div className="text-xs mt-10 text-secondary">
                  To sign the form, please accept the
                  <span style={{ color: 'blue' }} className="underline cursor-pointer" onClick={onOpenTermsAndConditionsPopup}>
                    {' Terms & Conditions'}
                  </span>
                </div> */}
                <div className="text-xs mt-10 text-secondary">
                  If you do not agree with the above details,
                  <span className="underline cursor-pointer" onClick={onOpenFeedbackPopup}>
                    {' click here to give your feedback'}
                  </span>
                </div>
              </>
            )
          }
        </div>
        <hr className="mt-10" />
        <div className="flex flex-col justify-center mt-10">
          <Footer
            deviceInfo={details?.deviceInfo || ''}
            ipAddress={details?.ipAddress || ''}
            updatedDate={details?.updatedDate || 0}
          />
          <div className="text-xs text-secondary">© Copyright 2024. paññã. All rights reserved.</div>
        </div>
      </FormProvider>
    </form>
  );
}

export default AuthForm;
